/**
 * Buscar en el historial (the owner, 2026-10-05): the sales, the orders and the purchases, found
 * by whatever somebody remembers of them.
 *
 * A customer back with a bottle has the receipt, or remembers the day, their own name, what they
 * bought; the owner going over a week wants its fiado, its transfers, one cashier's sales, the
 * orders a driver charged, what one supplier brought and what is still owed to them. So every
 * condition is optional and they add up: each one set narrows the list, none set is all of it,
 * the latest first. The whole log is on every phone, so this reads only what is here and never
 * waits on the server.
 */

import { parseTicket, sumarDias, type Centavos } from '@abastece/shared';
import { pendienteDeCompra, type Compra, type FormaPago, type FormaPagoVenta, type Pedido, type Producto, type Venta } from '@abastece/core';
import { formasDeLaVenta } from './pedidos.js';
import { coincide } from './texto.js';

/**
 * A stretch of days, as somebody asks for one: the buttons over a history, as «Contabilidad» has
 * them (the owner, 2026-10-06: «canned "yesterday", "last month" etc like the accounting filters»).
 * `elegir` is two dates of their own, picked on a calendar. Few enough to sit in a row or two with
 * no sideways scroll (the owner, the same day: «this should not have a horizontal scroll bar for
 * all the presets for date, just use fewer canned dates»): the last seven or thirty days and the
 * year went, the calendar finds those.
 */
export const PERIODOS = ['todo', 'hoy', 'ayer', 'mes', 'mesPasado', 'elegir'] as const;
export type Periodo = (typeof PERIODOS)[number];

/** The first and the last day, both counted in; null where it is open. */
export interface Rango {
  desde: string | null;
  hasta: string | null;
}

/** A period's days, as of `hoyEs`. The two dates of `elegir` are taken as typed, empty for open. */
export function rangoDe(periodo: Periodo, hoyEs: string, elegido: { desde: string; hasta: string }): Rango {
  switch (periodo) {
    case 'todo':
      return { desde: null, hasta: null };
    case 'hoy':
      return { desde: hoyEs, hasta: hoyEs };
    case 'ayer': {
      const ayer = sumarDias(hoyEs, -1);
      return { desde: ayer, hasta: ayer };
    }
    case 'mes':
      return { desde: `${hoyEs.slice(0, 7)}-01`, hasta: hoyEs };
    case 'mesPasado': {
      const fin = sumarDias(`${hoyEs.slice(0, 7)}-01`, -1);
      return { desde: `${fin.slice(0, 7)}-01`, hasta: fin };
    }
    case 'elegir': {
      const desde = elegido.desde || null;
      const hasta = elegido.hasta || null;
      // Picked the wrong way round, it is still the days between the two.
      return desde && hasta && desde > hasta ? { desde: hasta, hasta: desde } : { desde, hasta };
    }
  }
}

export const dentroDe = (fecha: string, r: Rango): boolean =>
  (r.desde === null || fecha >= r.desde) && (r.hasta === null || fecha <= r.hasta);

export const rangoAbierto = (r: Rango): boolean => r.desde === null && r.hasta === null;

/** A day as a date with no time of day, so no time zone can move it to the day before. */
const comoFecha = (fecha: string): Date => new Date(`${fecha}T00:00:00Z`);

/**
 * Two dates picked, short, as their button says them: «22 ago – 1 sep», one day alone as itself,
 * the year only when it is not this one's.
 */
export function etiquetaRango(desde: string, hasta: string, hoyEs: string, locale: string): string {
  const fmt = (fecha: string) =>
    new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      ...(fecha.slice(0, 4) === hoyEs.slice(0, 4) ? {} : { year: 'numeric' }),
      timeZone: 'UTC',
    }).format(comoFecha(fecha));
  if (!desde && !hasta) return '';
  if (!hasta || desde === hasta) return fmt(desde || hasta);
  if (!desde) return fmt(hasta);
  return `${fmt(desde)} – ${fmt(hasta)}`;
}

/** The month a day is in, `YYYY-MM`, and the months either side of it. */
export const mesDe = (fecha: string): string => fecha.slice(0, 7);
export function otroMes(mes: string, cuantos: number): string {
  const [y, m] = mes.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + cuantos, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * A month as its calendar shows it, Sunday first as Nicaragua's are: its days in weeks of seven,
 * null before the first and after the last.
 */
export function semanasDelMes(mes: string): (string | null)[][] {
  const [y, m] = mes.split('-').map(Number) as [number, number];
  const primero = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const dias = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const celdas: (string | null)[] = [
    ...Array<null>(primero).fill(null),
    ...Array.from({ length: dias }, (_, i) => `${mes}-${String(i + 1).padStart(2, '0')}`),
  ];
  while (celdas.length % 7 !== 0) celdas.push(null);
  return Array.from({ length: celdas.length / 7 }, (_, i) => celdas.slice(i * 7, i * 7 + 7));
}

/**
 * A day tapped on the calendar: the first of the two, or the last; before the first, the two swap;
 * once both are there, it starts again.
 */
export function tocarDia(elegido: { desde: string; hasta: string }, dia: string): { desde: string; hasta: string } {
  if (!elegido.desde || elegido.hasta) return { desde: dia, hasta: '' };
  return dia < elegido.desde ? { desde: dia, hasta: elegido.desde } : { desde: elegido.desde, hasta: dia };
}

/** The ways a sale is paid, in the order the till offers them: what «Forma de pago» lists. */
export const FORMAS_DE_PAGO: readonly FormaPagoVenta[] = ['efectivo_cordobas', 'efectivo_dolares', 'tarjeta', 'transferencia', 'billetera', 'credito'];

/**
 * The words in the box are this ticket: as the receipt prints it, or just its number. That one goes
 * first, being the one asked for; the box still finds the others its digits are part of.
 */
export function esElTicket(ticket: string, consulta: string): boolean {
  const q = consulta.trim().toUpperCase();
  if (!q) return false;
  if (q === ticket.toUpperCase()) return true;
  return /^\d+$/.test(q) && parseTicket(ticket)?.seq === Number(q);
}

/** A product as the box knows it: by the name on the receipt and by both of today's. */
type Nombres = Pick<Producto, 'nombre' | 'nombreEn'>;
const nombresDe = (p: Nombres | undefined): string[] => (p ? [p.nombre, p.nombreEn ?? ''] : []);

// ---------------------------------------------------------------------------
// Las ventas
// ---------------------------------------------------------------------------

export type EstadoVenta = 'todas' | 'validas' | 'anuladas' | 'conDevolucion' | 'porConfirmar';
export const ESTADOS_VENTA: readonly EstadoVenta[] = ['todas', 'validas', 'anuladas', 'conDevolucion', 'porConfirmar'];

/** Where a sale came from: the counter, an order brought, one with a delivery cost, a quick sale in it. */
export type TipoVenta = 'todas' | 'mostrador' | 'pedido' | 'conEnvio' | 'rapida';
export const TIPOS_VENTA: readonly TipoVenta[] = ['todas', 'mostrador', 'pedido', 'conEnvio', 'rapida'];

export interface FiltroVentas {
  /** The box: ticket, client, product, every word somewhere. */
  texto: string;
  periodo: Periodo;
  /** The two dates of `elegir`, as typed; empty for open. */
  desde: string;
  hasta: string;
  /** Empty for anybody. */
  clienteId: string;
  /** Empty for any. Fiado is `credito`. */
  forma: FormaPagoVenta | '';
  estado: EstadoVenta;
  tipo: TipoVenta;
  /** Who charged it, from the sale's own event; empty for anybody. */
  quien: string;
}

export const SIN_FILTROS_VENTAS: FiltroVentas = {
  texto: '',
  periodo: 'todo',
  desde: '',
  hasta: '',
  clienteId: '',
  forma: '',
  estado: 'todas',
  tipo: 'todas',
  quien: '',
};

/** Anything back from a sale yet. Even something given away free counts: it came back. */
export const tieneDevoluciones = (v: Venta): boolean =>
  (v.devuelto ?? 0) > 0 || v.lineas.some((l) => (l.devuelta ?? 0) > 0);

function estadoCoincide(v: Venta, estado: EstadoVenta): boolean {
  switch (estado) {
    case 'todas':
      return true;
    case 'validas':
      return !v.anulada;
    case 'anuladas':
      return v.anulada;
    case 'conDevolucion':
      return tieneDevoluciones(v);
    case 'porConfirmar':
      return !v.anulada && v.pagos.some((p) => p.estado === 'pendiente');
  }
}

function tipoCoincide(v: Venta, tipo: TipoVenta): boolean {
  switch (tipo) {
    case 'todas':
      return true;
    case 'mostrador':
      return v.pedidoId === null;
    case 'pedido':
      return v.pedidoId !== null;
    case 'conEnvio':
      return (v.envio ?? 0) > 0;
    case 'rapida':
      return v.lineas.some((l) => l.productoId === null);
  }
}

/** What the box looks through on a sale: its ticket, its client, and each line's names. */
export interface ContextoVentas {
  clientes: ReadonlyMap<string, string>;
  productos: ReadonlyMap<string, Nombres>;
  /** The sales the person in `quien` charged; undefined while still being read, which finds none. */
  deQuien?: ReadonlySet<string>;
}

function textoDeVenta(v: Venta, ctx: ContextoVentas): string {
  return [
    v.ticket,
    (v.clienteId && ctx.clientes.get(v.clienteId)) || '',
    ...v.lineas.flatMap((l) => [l.descripcion, ...nombresDe(l.productoId ? ctx.productos.get(l.productoId) : undefined)]),
  ].join(' ');
}

/** Every condition set holds for this sale. `rango` is the period's days, worked out once. */
export function ventaCoincide(v: Venta, f: FiltroVentas, rango: Rango, ctx: ContextoVentas): boolean {
  if (!dentroDe(v.fecha, rango)) return false;
  if (f.clienteId && v.clienteId !== f.clienteId) return false;
  if (f.forma && !formasDeLaVenta(v).includes(f.forma)) return false;
  if (!estadoCoincide(v, f.estado) || !tipoCoincide(v, f.tipo)) return false;
  if (f.quien && !ctx.deQuien?.has(v.id)) return false;
  return !f.texto.trim() || esElTicket(v.ticket, f.texto) || coincide(textoDeVenta(v, ctx), f.texto);
}

/** The conditions set besides the box, one for each the «Filtros» button counts. */
export function filtrosDeVentas(f: FiltroVentas, rango: Rango): (keyof FiltroVentas)[] {
  const puestos: (keyof FiltroVentas)[] = [];
  if (!rangoAbierto(rango)) puestos.push('periodo');
  if (f.clienteId) puestos.push('clienteId');
  if (f.forma) puestos.push('forma');
  if (f.estado !== 'todas') puestos.push('estado');
  if (f.tipo !== 'todas') puestos.push('tipo');
  if (f.quien) puestos.push('quien');
  return puestos;
}

/** The ticket asked for first, then the latest day, then the latest made. */
export function ordenarVentas(ventas: readonly Venta[], texto: string): Venta[] {
  return [...ventas].sort(
    (a, b) =>
      Number(esElTicket(b.ticket, texto)) - Number(esElTicket(a.ticket, texto)) ||
      b.fecha.localeCompare(a.fecha) ||
      b.id.localeCompare(a.id),
  );
}

/** How many were found, and what those not voided came to: what a day's sales are read by. */
export function resumenVentas(ventas: readonly Venta[]): { n: number; total: Centavos } {
  return { n: ventas.length, total: ventas.reduce((a, v) => (v.anulada ? a : a + v.total), 0) as Centavos };
}

// ---------------------------------------------------------------------------
// Los pedidos
// ---------------------------------------------------------------------------

export interface FiltroPedidos {
  /** The box: client, product, note, the ticket that charged it, why it was cancelled. */
  texto: string;
  periodo: Periodo;
  desde: string;
  hasta: string;
  clienteId: string;
  /** The rest are the delivered ones', read off the sale that charged each. */
  forma: FormaPagoVenta | '';
  quien: string;
  conEnvio: boolean;
}

export const SIN_FILTROS_PEDIDOS: FiltroPedidos = {
  texto: '',
  periodo: 'todo',
  desde: '',
  hasta: '',
  clienteId: '',
  forma: '',
  quien: '',
  conEnvio: false,
};

export interface ContextoPedidos {
  clientes: ReadonlyMap<string, string>;
  productos: ReadonlyMap<string, Nombres>;
  /** The sales behind the delivered, and who charged each: undefined until read. */
  ventas?: ReadonlyMap<string, Venta>;
  quien?: ReadonlyMap<string, string>;
}

/**
 * The day an order goes by, the one its card shows: the day it was charged once it was, the day it
 * is due until then.
 */
export const fechaDelPedido = (p: Pedido, ventas?: ReadonlyMap<string, Venta>): string =>
  (p.ventaId ? ventas?.get(p.ventaId)?.fecha : undefined) ?? p.fechaEntrega;

/** Every condition set holds for this order. Those of the delivered hold for no other. */
export function pedidoCoincide(p: Pedido, f: FiltroPedidos, rango: Rango, ctx: ContextoPedidos): boolean {
  const venta = p.ventaId ? ctx.ventas?.get(p.ventaId) : undefined;
  if (!dentroDe(fechaDelPedido(p, ctx.ventas), rango)) return false;
  if (f.clienteId && p.clienteId !== f.clienteId) return false;
  if (p.estado === 'entregado') {
    if (f.forma && !(venta && formasDeLaVenta(venta).includes(f.forma))) return false;
    if (f.quien && !(p.ventaId && ctx.quien?.get(p.ventaId) === f.quien)) return false;
    if (f.conEnvio && !((venta?.envio ?? 0) > 0)) return false;
  }
  if (!f.texto.trim()) return true;
  const texto = [
    ctx.clientes.get(p.clienteId) ?? '',
    p.nota ?? '',
    p.motivoCancelacion ?? '',
    venta?.ticket ?? '',
    ...p.lineas.flatMap((l) => nombresDe(ctx.productos.get(l.productoId))),
  ].join(' ');
  return coincide(texto, f.texto);
}

/** The conditions set besides the box; those of the delivered only on «Entregados», where they apply. */
export function filtrosDePedidos(f: FiltroPedidos, rango: Rango, entregados: boolean): (keyof FiltroPedidos)[] {
  const puestos: (keyof FiltroPedidos)[] = [];
  if (!rangoAbierto(rango)) puestos.push('periodo');
  if (f.clienteId) puestos.push('clienteId');
  if (entregados && f.forma) puestos.push('forma');
  if (entregados && f.quien) puestos.push('quien');
  if (entregados && f.conEnvio) puestos.push('conEnvio');
  return puestos;
}

// ---------------------------------------------------------------------------
// Las compras
// ---------------------------------------------------------------------------

/** «Por pagar»: bought on credit and not all paid yet, as «Cuentas por pagar» counts it. */
export type EstadoCompra = 'todas' | 'validas' | 'anuladas' | 'conDevolucion' | 'porPagar';
export const ESTADOS_COMPRA: readonly EstadoCompra[] = ['todas', 'validas', 'anuladas', 'conDevolucion', 'porPagar'];

/** The ways a purchase is paid, in the order «Mercadería comprada» offers them. */
export const FORMAS_DE_COMPRA: readonly FormaPago[] = ['efectivo_cordobas', 'efectivo_dolares', 'transferencia', 'billetera', 'credito'];

export interface FiltroCompras {
  /** The box: supplier, invoice, product, every word somewhere. */
  texto: string;
  periodo: Periodo;
  desde: string;
  hasta: string;
  /** Empty for any. */
  proveedorId: string;
  /** Empty for any. On credit is `credito`. */
  forma: FormaPago | '';
  estado: EstadoCompra;
}

export const SIN_FILTROS_COMPRAS: FiltroCompras = {
  texto: '',
  periodo: 'todo',
  desde: '',
  hasta: '',
  proveedorId: '',
  forma: '',
  estado: 'todas',
};

/** Anything sent back to the supplier from it yet. */
export const tieneDevolucionesCompra = (c: Compra): boolean =>
  (c.devuelto ?? 0) > 0 || c.lineas.some((l) => (l.devuelta ?? 0) > 0);

/**
 * How a purchase was paid, as its card says it: each part paid with it, and its own way while the
 * parts left that something to pay. Voided, it was still paid that way; a part voided on its own
 * was not paid at all. What was paid after it, of what was owed, paid a debt: the purchase was on
 * credit.
 */
export function formasDeLaCompra(c: Compra): FormaPago[] {
  const partes = (c.pagos ?? []).filter((x) => x.conLaCompra && (!x.anulado || x.anuladoConLaCompra));
  const resto = partes.reduce((a, x) => a - x.monto, c.totalCordobas as number);
  const formas: FormaPago[] = partes.map((x) => x.formaPago);
  if (partes.length === 0 || resto > 0) formas.push(c.formaPago);
  return [...new Set(formas)];
}

function estadoDeCompra(c: Compra, estado: EstadoCompra): boolean {
  switch (estado) {
    case 'todas':
      return true;
    case 'validas':
      return !c.anulada;
    case 'anuladas':
      return c.anulada;
    case 'conDevolucion':
      return tieneDevolucionesCompra(c);
    case 'porPagar':
      return pendienteDeCompra(c) > 0;
  }
}

/** What the box looks through on a purchase: its supplier, its invoice, each line's names, why it was voided. */
export interface ContextoCompras {
  proveedores: ReadonlyMap<string, string>;
  productos: ReadonlyMap<string, Nombres>;
}

/** The words in the box are this invoice, as it is written on it: that one goes first. */
export function esLaFactura(documento: string | null, consulta: string): boolean {
  const q = consulta.trim().toUpperCase();
  return q !== '' && documento !== null && documento.trim().toUpperCase() === q;
}

/** Every condition set holds for this purchase. `rango` is the period's days, worked out once. */
export function compraCoincide(c: Compra, f: FiltroCompras, rango: Rango, ctx: ContextoCompras): boolean {
  if (!dentroDe(c.fecha, rango)) return false;
  if (f.proveedorId && c.proveedorId !== f.proveedorId) return false;
  if (f.forma && !formasDeLaCompra(c).includes(f.forma)) return false;
  if (!estadoDeCompra(c, f.estado)) return false;
  if (!f.texto.trim()) return true;
  const texto = [
    ctx.proveedores.get(c.proveedorId) ?? '',
    c.documento ?? '',
    c.motivoAnulacion ?? '',
    ...c.lineas.flatMap((l) => nombresDe(ctx.productos.get(l.productoId))),
  ].join(' ');
  return coincide(texto, f.texto);
}

/** The conditions set besides the box, one for each the «Filtros» button counts. */
export function filtrosDeCompras(f: FiltroCompras, rango: Rango): (keyof FiltroCompras)[] {
  const puestos: (keyof FiltroCompras)[] = [];
  if (!rangoAbierto(rango)) puestos.push('periodo');
  if (f.proveedorId) puestos.push('proveedorId');
  if (f.forma) puestos.push('forma');
  if (f.estado !== 'todas') puestos.push('estado');
  return puestos;
}

/** The invoice asked for first, then the latest day, then the latest entered. */
export function ordenarCompras(compras: readonly Compra[], texto: string): Compra[] {
  return [...compras].sort(
    (a, b) =>
      Number(esLaFactura(b.documento, texto)) - Number(esLaFactura(a.documento, texto)) ||
      b.fecha.localeCompare(a.fecha) ||
      b.id.localeCompare(a.id),
  );
}

/** How many were found, and what those not voided came to, in córdobas. */
export function resumenCompras(compras: readonly Compra[]): { n: number; total: Centavos } {
  return { n: compras.length, total: compras.reduce((a, c) => (c.anulada ? a : a + c.totalCordobas), 0) as Centavos };
}
